export type CurrentJob = {
    id?: string | number;
    companyId?: number | string | null;
    companyName?: string;
    logo?: string;
    instituteId?: number | string | null;
    otherInstitute?: string;
    instituteName?: string;
    instituteLogo?: string;
    designation?: string;
    startDate?: string;
    isPrimary?: number;
    jobCategory?: string;
    isPrestigious?: number;
    founderStatus?: string;
    industryCode?: string;
    annualRevenue?: string;
    establishmentYear?: number | string;
};

export function parseCurrentJobs(raw: unknown): CurrentJob[] {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw as CurrentJob[];
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

const isPrimaryJob = (job: CurrentJob) =>
    Number(job.isPrimary ?? (job as { isPrimaryCurrent?: number | boolean }).isPrimaryCurrent) === 1;

const latestByStartDate = (jobs: CurrentJob[]) =>
    [...jobs].sort((a, b) => String(b.startDate || '').localeCompare(String(a.startDate || '')))[0];

// Profile working status IN_DEFENCE is stored on the job as DEFENCE.
const matchesWorkingStatus = (job: CurrentJob, workingStatusCode?: string) => {
    if (!workingStatusCode || !job.jobCategory) return false;
    if (workingStatusCode === 'IN_DEFENCE') return job.jobCategory === 'DEFENCE' || job.jobCategory === 'IN_DEFENCE';
    return job.jobCategory === workingStatusCode;
};

// 1. isPrimary / isPrimaryCurrent = 1
// 2. jobCategory matches profile workingStatusCode (null category does not match)
// 3. latest start date, same rule the card already used for old data
export function pickDisplayJob(
    raw: unknown,
    jobCategory?: string,
    workingStatusCode?: string
): { job: CurrentJob | null; count: number } {
    let jobs = parseCurrentJobs(raw);
    if (jobCategory) jobs = jobs.filter((job) => job.jobCategory === jobCategory);
    if (!jobs.length) return { job: null, count: 0 };
    // One current job is the card, even when isPrimary was never set.
    if (jobs.length === 1) return { job: jobs[0], count: 1 };

    const primary = jobs.find(isPrimaryJob);
    if (primary) return { job: primary, count: jobs.length };

    const statusMatches = jobs.filter((job) => matchesWorkingStatus(job, workingStatusCode));
    if (statusMatches.length) return { job: latestByStartDate(statusMatches), count: jobs.length };

    return { job: latestByStartDate(jobs), count: jobs.length };
}

const hasId = (id: unknown) => id !== null && id !== undefined && id !== '' && Number(id) !== 0;

// Teaching and research is an institute, not a company. instituteId is looked up;
// when it is empty the card uses otherInstitute from that same job.
export function jobPlace(
    job: CurrentJob,
    lookup: (id: any, domain: string) => string
): { name: string; logo?: string; kind: 'institute' | 'company' } {
    if (job.jobCategory === 'TEACHING_AND_RESEARCH') {
        if (hasId(job.instituteId)) {
            const resolved = lookup(job.instituteId, 'INSTITUTE_DOMAIN');
            const name =
                resolved && String(resolved) !== String(job.instituteId)
                    ? resolved
                    : job.instituteName || job.otherInstitute || '';
            return { name, logo: job.instituteLogo, kind: 'institute' };
        }
        return { name: job.otherInstitute || job.instituteName || '', logo: job.instituteLogo, kind: 'institute' };
    }

    if (hasId(job.companyId)) {
        const resolved = lookup(job.companyId, 'COMPANY_CODE');
        if (resolved && String(resolved) !== String(job.companyId)) {
            return { name: resolved, logo: job.logo, kind: 'company' };
        }
    }
    return { name: job.companyName || '', logo: job.logo, kind: 'company' };
}

